import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { XMarkIcon, ArrowPathIcon, UserPlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { tasksAPI, teamsAPI, usersAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

interface RecurringTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  recurringTask: any | null;
  onSave: () => void;
  teams: any[];
  users: any[];
  projects: any[];
}

const RecurringTaskModal: React.FC<RecurringTaskModalProps> = ({
  isOpen,
  onClose,
  recurringTask,
  onSave,
  teams,
  users,
  projects,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'NORMAL',
    status: 'TODO',
    estimatedHours: '',
    recurrenceType: 'WEEKLY',
    recurrenceValue: 1,
    daysOfWeek: [] as number[],
    dayOfMonth: '',
    endDate: '',
    maxOccurrences: '',
    teamId: '',
    projectId: '',
    isActive: true,
  });
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (recurringTask) {
      setFormData({
        title: recurringTask.title || '',
        description: recurringTask.description || '',
        priority: recurringTask.priority || 'NORMAL',
        status: recurringTask.status || 'TODO',
        estimatedHours: recurringTask.estimatedHours?.toString() || '',
        recurrenceType: recurringTask.recurrenceType || 'WEEKLY',
        recurrenceValue: recurringTask.recurrenceValue || 1,
        daysOfWeek: recurringTask.daysOfWeek ? JSON.parse(recurringTask.daysOfWeek) : [],
        dayOfMonth: recurringTask.dayOfMonth?.toString() || '',
        endDate: recurringTask.endDate ? new Date(recurringTask.endDate).toISOString().split('T')[0] : '',
        maxOccurrences: recurringTask.maxOccurrences?.toString() || '',
        teamId: recurringTask.teamId || '',
        projectId: recurringTask.projectId || '',
        isActive: recurringTask.isActive ?? true,
      });
      setAssigneeIds(recurringTask.assignees?.map((a: any) => a.userId) || []);
    } else {
      // Reset form for new recurring task
      setFormData({
        title: '',
        description: '',
        priority: 'NORMAL',
        status: 'TODO',
        estimatedHours: '',
        recurrenceType: 'WEEKLY',
        recurrenceValue: 1,
        daysOfWeek: [],
        dayOfMonth: '',
        endDate: '',
        maxOccurrences: '',
        teamId: '',
        projectId: '',
        isActive: true,
      });
      setAssigneeIds([]);
    }
  }, [recurringTask]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Clean up the form data to avoid validation issues
      const cleanedData = {
        ...formData,
        // Convert empty strings to null for optional fields
        description: formData.description || null,
        estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : null,
        dayOfMonth: formData.dayOfMonth ? parseInt(formData.dayOfMonth) : null,
        maxOccurrences: formData.maxOccurrences ? parseInt(formData.maxOccurrences) : null,
        endDate: formData.endDate || null,
        teamId: formData.teamId || null,
        projectId: formData.projectId || null,
        daysOfWeek: formData.daysOfWeek,
        assigneeIds,
        // Include creatorId (use existing creator for updates, current user for new tasks)
        creatorId: recurringTask?.creatorId || recurringTask?.creator?.id || user?.id,
      };

      // Validate that we have a creatorId
      if (!cleanedData.creatorId) {
        toast.error('Unable to determine creator. Please try again.');
        return;
      }

      // Remove null/undefined values to avoid sending them to the backend
      const submitData = Object.fromEntries(
        Object.entries(cleanedData).filter(([_, value]) => value !== null && value !== undefined && value !== '')
      );

      if (recurringTask) {
        await tasksAPI.updateRecurringTask(recurringTask.id, submitData);
        toast.success('Recurring task updated successfully');
      } else {
        await tasksAPI.createRecurringTask(submitData);
        toast.success('Recurring task created successfully');
      }

      onSave();
      onClose();
    } catch (error: any) {
      console.error('❌ [RecurringTaskModal] Error saving recurring task:', error);
      console.error('❌ [RecurringTaskModal] Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        config: error?.config
      });
      toast.error(`Failed to save recurring task: ${error?.response?.data?.message || error?.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDayOfWeekToggle = (day: number) => {
    setFormData(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day].sort()
    }));
  };

  const handleAssigneeToggle = (userId: string) => {
    setAssigneeIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <ArrowPathIcon className="w-6 h-6 text-blue-500" />
            <h2 className="text-xl font-semibold text-gray-900">
              {recurringTask ? 'Edit Recurring Task' : 'Create Recurring Task'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="LOW">Low</option>
                <option value="NORMAL">Normal</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Recurrence Settings */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Recurrence Settings</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recurrence Type
                </label>
                <select
                  value={formData.recurrenceType}
                  onChange={(e) => setFormData(prev => ({ ...prev, recurrenceType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="DAILY">Daily</option>
                  <option value="WEEKLY">Weekly</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="YEARLY">Yearly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Every
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.recurrenceValue}
                  onChange={(e) => setFormData(prev => ({ ...prev, recurrenceValue: parseInt(e.target.value) || 1 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="TODO">To Do</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="REVIEW">Review</option>
                  <option value="DONE">Done</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Days of Week for Weekly recurrence */}
            {formData.recurrenceType === 'WEEKLY' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Days of Week
                </label>
                <div className="flex flex-wrap gap-2">
                  {dayNames.map((day, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleDayOfWeekToggle(index)}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        formData.daysOfWeek.includes(index)
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {day.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Day of Month for Monthly recurrence */}
            {formData.recurrenceType === 'MONTHLY' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day of Month (1-31)
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={formData.dayOfMonth}
                  onChange={(e) => setFormData(prev => ({ ...prev, dayOfMonth: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>

          {/* Assignment and Project */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Assignment & Project</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team
                </label>
                <select
                  value={formData.teamId}
                  onChange={(e) => setFormData(prev => ({ ...prev, teamId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project
                </label>
                <select
                  value={formData.projectId}
                  onChange={(e) => setFormData(prev => ({ ...prev, projectId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No Project (Standalone)</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Assignees */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assignees
              </label>
              <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
                {users.map(user => (
                  <label key={user.id} className="flex items-center space-x-3 py-1">
                    <input
                      type="checkbox"
                      checked={assigneeIds.includes(user.id)}
                      onChange={() => handleAssigneeToggle(user.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      {user.firstName} {user.lastName} ({user.username})
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Additional Settings */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Settings</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated Hours
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.estimatedHours}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimatedHours: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Occurrences
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.maxOccurrences}
                  onChange={(e) => setFormData(prev => ({ ...prev, maxOccurrences: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Active (task will appear on calendar)</span>
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
            >
              {isLoading ? 'Saving...' : (recurringTask ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default RecurringTaskModal;
